import { FormElementTypeId, Grid, Column, SubColumn, FormElementGridCellAnswer } from '@packages/models/api';
import { GridCell } from './useGridElement';

interface CellInputRules {
    maxLength: number;
    pattern: RegExp;
}

const DYNAMIC_GRID_MAX_CHAR_LENGTH = 12;
const VALVE_CLEARANCE_GRID_MAX_CHAR_LENGTH = 15;
const PRESSURE_TEST_GRID_MAX_CHAR_LENGTH = 4;

export function formatWithCommas(value: string): string {
    if (!value) {
        return '';
    }
    const num = parseInt(value, 10);
    if (isNaN(num)) {
        return value;
    }
    return num.toLocaleString('en-US');
}

/**
 * Cell Input Rules
 *
 * DynamicGrid:          max 12 characters, alphanumeric, spaces, hyphens, percent, and decimal (e.g. "ABC-123 45% 1.25")
 * ValveClearanceGrid:   max 15 characters, alphanumeric, decimal, and spaces (e.g. "0.123 mm", "12.5 inches")
 * PressureTestGrid:     max 4 digits, numbers only (e.g. "1234")
 */
const CELL_INPUT_RULES: Record<string, CellInputRules> = {
    [FormElementTypeId.DynamicGrid]: {
        maxLength: DYNAMIC_GRID_MAX_CHAR_LENGTH,
        pattern: /[^a-zA-Z0-9 %.\-]/g,
    },
    [FormElementTypeId.ValveClearanceGrid]: {
        maxLength: VALVE_CLEARANCE_GRID_MAX_CHAR_LENGTH,
        pattern: /[^a-zA-Z0-9.\s]/g,
    },
    [FormElementTypeId.PressureTestGrid]: {
        maxLength: PRESSURE_TEST_GRID_MAX_CHAR_LENGTH,
        pattern: /[^0-9]/g,
    },
};

export function formatCellInput(formElementTypeId: FormElementTypeId, value: string): string {
    const rules = CELL_INPUT_RULES[formElementTypeId];
    if (!rules) {
        return value;
    }

    let formatted = value;

    if (rules.pattern) {
        formatted = formatted.replace(rules.pattern, '');
    }

    return formatted.slice(0, rules.maxLength);
}

export function getGridCells(formElementTypeId: FormElementTypeId, grid: Grid[]): Column[] | SubColumn[] {
    switch (formElementTypeId) {
        case FormElementTypeId.DynamicGrid:
            return grid.flatMap((row) => row.columns ?? []);

        case FormElementTypeId.PressureTestGrid:
            return grid.flatMap((row) => (row.subRows ?? []).flatMap((subRow) => subRow.columns ?? []));

        case FormElementTypeId.VehicleNoiseGrid:
        case FormElementTypeId.ValveClearanceGrid:
            return grid.flatMap((row) => (row.columns ?? []).flatMap((column) => column.subColumns ?? []));

        default:
            return [];
    }
}

export function filterNonBlankAnswers(cellAnswers: FormElementGridCellAnswer[]): FormElementGridCellAnswer[] {
    return cellAnswers.filter((cellAnswer) => cellAnswer.value.trim() !== '');
}

export function getRequiredCellIds(formElementTypeId: FormElementTypeId, grid: Grid[]): string[] {
    return getGridCells(formElementTypeId, grid)
        .filter((cell) => cell.isRequired)
        .map((cell) => (cell as GridCell).worksheetElementGridId);
}

export function isGridValid(
    elementLevelRequired: boolean,
    requiredCellIds: string[],
    cellAnswers: FormElementGridCellAnswer[]
): boolean {
    const nonBlankAnswers = filterNonBlankAnswers(cellAnswers);

    if (requiredCellIds.length > 0) {
        const answeredRequiredCount = requiredCellIds.filter((cellId) =>
            nonBlankAnswers.some((answer) => answer.worksheetElementGridId === cellId)
        ).length;
        return answeredRequiredCount >= requiredCellIds.length;
    }

    if (elementLevelRequired) {
        return nonBlankAnswers.length > 0;
    }

    return true;
}

export function hasUnansweredRequiredCells(
    formElementTypeId: FormElementTypeId,
    grid: Grid[] | undefined,
    worksheetGridCellAnswers: FormElementGridCellAnswer[] | undefined,
    elementRequired: boolean
): boolean {
    if (!grid?.length) {
        return elementRequired;
    }

    const requiredCellIds = getRequiredCellIds(formElementTypeId, grid);
    const elementLevelRequired = elementRequired && requiredCellIds.length === 0;

    return !isGridValid(elementLevelRequired, requiredCellIds, worksheetGridCellAnswers ?? []);
}

export function formatToggleAccessibilityLabel(isExpanded: boolean, formElementLabel?: string): string {
    const label = (formElementLabel ?? 'question').replace(/\n/g, ' ').trim();
    const truncated = label.length > 60 ? `${label.slice(0, 60)}…` : label;
    return `${isExpanded ? 'Collapse' : 'Expand'} ${truncated}`;
}
